import { useCallback, useEffect, useState } from "react";

import { apiClient } from "../api/client.js";
import { useAuth } from "../auth/AuthContext.js";

interface Transaction {
  id: string;
  device_name: string;
  amount_pence: number;
  currency: string;
  duration_minutes: number;
  status: string;
  starts_at?: string;
  ends_at?: string;
  created_at: string;
}

function formatPrice(pence: number): string {
  return `£${(pence / 100).toFixed(2)}`;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function statusBadge(status: string) {
  switch (status) {
    case "succeeded":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-green-500/20 text-green-400">
          Paid
        </span>
      );
    case "refund_due":
    case "refunded":
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-red-500/20 text-red-400">
          Refunded
        </span>
      );
    default:
      return (
        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded-full bg-gray-500/20 text-gray-400">
          {status}
        </span>
      );
  }
}

export function Transactions() {
  const { selectedVenue } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTransactions = useCallback(async () => {
    if (!selectedVenue) return;
    try {
      const res = await apiClient["/api/v1/venue/transactions"]["get"]({
        parameters: { venue_id: selectedVenue.id },
        body: null,
      });
      if (res.code === 200) {
        setTransactions(res.body as Transaction[]);
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [selectedVenue]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const totalRevenue = transactions.reduce((sum, t) => sum + t.amount_pence, 0);

  return (
    <main className="p-6">
      <h1 className="text-2xl font-bold text-text-primary mb-6">Transactions</h1>

      {/* Summary stats */}
      {!loading && transactions.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-surface-raised border border-border rounded-lg p-4">
            <p className="text-sm text-text-muted mb-1">Total Revenue</p>
            <p className="text-2xl font-bold text-accent">{formatPrice(totalRevenue)}</p>
          </div>
          <div className="bg-surface-raised border border-border rounded-lg p-4">
            <p className="text-sm text-text-muted mb-1">Total Sessions</p>
            <p className="text-2xl font-bold text-text-primary">{transactions.length}</p>
          </div>
        </div>
      )}

      {loading && <p className="text-text-muted text-sm">Loading transactions...</p>}

      {!loading && transactions.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No transactions yet.</p>
          <p className="text-text-muted text-sm mt-1">
            Transactions will appear here when customers pay to play.
          </p>
        </div>
      )}

      {!loading && transactions.length > 0 && (
        <div className="grid gap-3">
          {transactions.map((t) => (
            <div
              key={t.id}
              className="bg-surface-raised border border-border rounded-lg p-4 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div>
                  <div className="font-medium text-text-primary">{t.device_name}</div>
                  <div className="text-sm text-text-muted">
                    {t.duration_minutes} min &middot; {formatTime(t.created_at)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                {statusBadge(t.status)}
                <span className="text-lg font-semibold text-text-primary">
                  {formatPrice(t.amount_pence)}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
